import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect, Dispatch } from 'react-redux';
// @ts-ignore
import { CSSTransitionGroup } from 'react-transition-group';
import { debounce } from 'lodash';
import cx from 'classnames';

import {
  changeQuery,
  loadGroupChats,
  loadPotentialChatMembers,
  createGroupChatRecipient,
  selectRecipient,
  flush,
  RECIPIENT_TYPES,
  searchGroupChats,
  State as RBMState,
} from 'reducers/roleBasedMessagingSearch';
import Chat from 'entities/Chat';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ChatStore from 'chat/stores/ChatStore';
import Dialog from 'components/Dialog';
import EventTracking from 'lib/EventTracking';
// @ts-ignore
import GroupMessageStarter from 'chat/components/GroupMessageStarter';
import Heading from 'components/Heading';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import { mountModal } from 'lib/modal';
import Row from 'components/Row';
import ScrollContainer from 'components/ScrollContainer';
import SearchBlock from 'components/SearchBlock';
import SettingsStore from 'stores/SettingsStore';
import { SEARCH_NETWORK_LIMIT_MS } from 'chat/constants/ChatConstants';
// @ts-ignore -going to be replaced soon-
import RecipientItem from './RecipientItem';
// @ts-ignore -going to be replaced soon-
import RecipientSelectionItem from './RecipientSelectionItem';
import Tooltip from 'components/Tooltip';
import { MessageType } from './SplitComposer';
import { UIEventContext } from './ComposerContext';
import { Recipient, RecipientCountsMap } from 'types/typescript/messagingTypes';
import MetricTracking, { MetricName, MetricTagValue, boolTagValue } from 'lib/MetricTracking';

import '../SplitComposer.scss';

type Props = {
  onContinue: Function;
  loadingGroupResults: boolean;
  loadingPeopleResults: boolean;
  messageType: MessageType;
  isUrgentMessage: boolean;
  uiEventContext: UIEventContext;
};

type ReduxDispatchProps = {
  // Redux dispatchers
  changeQuery: typeof changeQuery;
  flush: typeof flush;
  selectRecipient: typeof selectRecipient;
  loadGroupChats: typeof loadGroupChats;
  loadPotentialChatMembers: typeof loadPotentialChatMembers;
  searchGroupChats: typeof searchGroupChats;
};

type ReduxStateProps = {
  // Redux state
  query: string;
  results: Array<Recipient[]>;
  selection: Recipient[];
  districtIsSelected: boolean;
  savedMessage: string;
  totalRecipientCount: number;
  recipientCountsMap: RecipientCountsMap;
};

type State = {
  totalCharacterMutations: number;
};

export function DisabledTooltipLayer(props: { text: any; children: React.ReactNode }) {
  return (
    <LayerTrigger layer={<Tooltip>{props.text}</Tooltip>} placement="bottom" alignment="middle">
      <div>{props.children}</div>
    </LayerTrigger>
  );
}

class SelectFromList extends React.Component<Props & ReduxDispatchProps & ReduxStateProps, State> {
  constructor(props: Props & ReduxDispatchProps & ReduxStateProps) {
    super(props);
    this.state = {
      totalCharacterMutations: 0,
    };
  }

  componentDidMount() {
    EventTracking.trackKey('announcements.share_to.view', this.props.uiEventContext);
  }

  _searchBlock?: { handleClose: () => void };

  debouncedLoadPotentialChatMembers = debounce((query: string) => {
    const { loadPotentialChatMembers, searchGroupChats } = this.props;
    loadPotentialChatMembers(query);
    searchGroupChats(query);
  }, SEARCH_NETWORK_LIMIT_MS);

  handleQueryChange = debounce(
    (rawQuery: string = '') => {
      this.setState({ totalCharacterMutations: this.state.totalCharacterMutations + 1 });
      const query = rawQuery.trim();
      if (query !== '') {
        this.debouncedLoadPotentialChatMembers(query);
      }
      if (query === '') {
        this.props.changeQuery(query);
      }
    },
    500,
    { leading: false, trailing: true }
  );

  renderHeader = (title: string, options: { disabled?: boolean; hidden: boolean }) => {
    if (options.hidden) {
      return null;
    } else {
      return (
        <Heading
          align="left"
          level="3"
          className={cx('SelectFromList-resultHeader', {
            // prettier-ignore
            'SelectFromList-resultHeader--disabled': options.disabled
          })}>
          {title}
        </Heading>
      );
    }
  };

  nonDistrictRecipientIsSelected = () => {
    const { selection, districtIsSelected } = this.props;
    return !!selection.length && !districtIsSelected;
  };

  getRecipients = (
    results: Array<Recipient[]>,
    type: 'organization' | 'group' | 'groupChat' | 'individual'
  ): Recipient[][] => {
    switch (type) {
      case 'organization':
        return results.filter(result =>
          result.every(r => {
            return r.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT;
          })
        );
      case 'group':
        return results.filter(result =>
          result.every(r => {
            return r.type === RECIPIENT_TYPES.GROUP_RECIPIENT;
          })
        );
      case 'groupChat':
        return results.filter(result =>
          result.every(r => {
            return r.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT;
          })
        );
      case 'individual':
        return results.filter(result =>
          result.every(r => {
            return r.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT;
          })
        );
      default:
        console.error('SelectFromList - getRecipients called without type');
        return [];
    }
  };

  checkIfRecipientIsSelected = (recipient: Recipient[]) => {
    const recipientIds = recipient.map(r => r.id);
    const selectionIds = this.props.selection.map(s => s.id);

    return !!recipientIds.filter(rId => selectionIds.indexOf(rId) !== -1).length;
  };

  toggleNewGroupChatModal = () => {
    const { loadGroupChats, selectRecipient, uiEventContext } = this.props;
    const additionalChatMembersLimit = SettingsStore.getChatMembersLimit() - 1;
    EventTracking.trackKey('announcements.share_to.group_chat.add_new.click', uiEventContext);
    const unmountModal = mountModal(
      <Dialog
        title="Create a group conversation"
        size="large"
        subtext={`Start the dialogue with up to ${additionalChatMembersLimit} other people—everyone can participate in the conversation.`}
        textAlign="left">
        <BoxBody>
          <GroupMessageStarter
            additionalChatMembersLimit={additionalChatMembersLimit}
            createRemoteChat
            onCreateRemoteChat={(chat: Chat) => {
              const recipient = createGroupChatRecipient(chat);
              selectRecipient(recipient, undefined);
              loadGroupChats(ChatStore.getGroupChats());
            }}
            onRequestHide={() => {
              if (unmountModal) {
                unmountModal();
              }
            }}
          />
        </BoxBody>
      </Dialog>,
      this
    );
  };

  renderResultsList = () => {
    const {
      results,
      districtIsSelected,
      query,
      loadingPeopleResults,
      selection,
      messageType,
    } = this.props;

    const isUrgentMessage = messageType === 'urgent';

    // this is used for filtering out people that shouldn't be targets for urgent messages
    const getShouldShowRecipientFilter = (urgentMessageFlag: boolean) => {
      if (urgentMessageFlag === false) {
        // making very sure we don't do anything different for normal messages
        return () => true;
      } else {
        return (recipient: Recipient) => {
          if (recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
            return !!recipient.group.organization?.permissions?.canReceiveUrgentMessage;
          } else if (recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
            return recipient.organization.permissions.canReceiveUrgentMessage;
          }
          return false;
        };
      }
    };

    const shouldShowRecipientFilter = getShouldShowRecipientFilter(isUrgentMessage);
    const districtResults = this.getRecipients(results, 'organization').filter(result =>
      result.every(shouldShowRecipientFilter)
    );
    const schoolResults = this.getRecipients(results, 'group').filter(result =>
      result.every(r =>
        r.type === RECIPIENT_TYPES.GROUP_RECIPIENT
          ? r.group.type === 'organization' &&
            r.group.permissions?.can_post &&
            shouldShowRecipientFilter(r)
          : false
      )
    );
    const groupResults = this.getRecipients(results, 'group').filter(result =>
      result.every(r =>
        r.type === RECIPIENT_TYPES.GROUP_RECIPIENT
          ? r.group.type === 'owned' &&
            r.group.permissions?.can_post &&
            shouldShowRecipientFilter(r)
          : false
      )
    );

    const recentGroupChatResults = this.getRecipients(results, 'groupChat')
      .filter(result => result.every(shouldShowRecipientFilter))
      .slice(0, 3);

    const recentIndividualResults = this.getRecipients(results, 'individual')
      .filter(result => result.every(shouldShowRecipientFilter))
      .slice(0, 20);

    return (
      <React.Fragment>
        {districtResults && districtResults.length ? (
          <Row>
            {this.renderHeader('Districts', {
              disabled: this.nonDistrictRecipientIsSelected(),
              hidden: false,
            })}
            <ul>
              {districtResults.map(district => (
                <RecipientItem
                  key={district[0].id}
                  recipient={district}
                  recipientIsSelected={this.checkIfRecipientIsSelected(district)}
                  nonDistrictRecipientIsSelected={this.nonDistrictRecipientIsSelected()}
                  selectionLength={selection.length}
                  districtIsSelected={districtIsSelected}
                />
              ))}
            </ul>
          </Row>
        ) : null}

        {schoolResults && schoolResults.length ? (
          <Row>
            {this.renderHeader('Schools', { disabled: districtIsSelected, hidden: false })}
            <ul>
              {schoolResults.map(school => (
                <RecipientItem
                  key={school[0].id}
                  recipient={school}
                  recipientIsSelected={this.checkIfRecipientIsSelected(school)}
                  selectionLength={selection.length}
                  districtIsSelected={districtIsSelected}
                />
              ))}
            </ul>
          </Row>
        ) : null}

        {groupResults && groupResults.length ? (
          <Row>
            {this.renderHeader('Classes', { disabled: districtIsSelected, hidden: false })}
            <ul>
              {groupResults.map(group => (
                <RecipientItem
                  key={group[0].id}
                  recipient={group}
                  recipientIsSelected={this.checkIfRecipientIsSelected(group)}
                  selectionLength={selection.length}
                  districtIsSelected={districtIsSelected}
                />
              ))}
            </ul>
          </Row>
        ) : null}

        {((recentGroupChatResults && recentGroupChatResults.length) || !query) && (
          <Row>
            <MediaObject figure="right" center>
              {this.renderHeader('Recent Group Conversations', {
                disabled: districtIsSelected,
                hidden: isUrgentMessage,
              })}
              <Heading
                level="3"
                className={cx('SelectFromList-resultHeader SelectFromList-resultHeader--right', {
                  // prettier-ignore
                  'SelectFromList-resultHeader--disabled': districtIsSelected
                })}>
                {isUrgentMessage === false ? (
                  <Link onClick={this.toggleNewGroupChatModal}>Create New</Link>
                ) : null}
              </Heading>
            </MediaObject>
            {recentGroupChatResults && recentGroupChatResults.length ? (
              <ul>
                {recentGroupChatResults.map(groupChat => (
                  <RecipientItem
                    key={groupChat[0].id}
                    recipient={groupChat}
                    recipientIsSelected={this.checkIfRecipientIsSelected(groupChat)}
                    selectionLength={selection.length}
                    districtIsSelected={districtIsSelected}
                  />
                ))}
              </ul>
            ) : null}
          </Row>
        )}

        {recentIndividualResults && recentIndividualResults.length && isUrgentMessage === false ? (
          <React.Fragment>
            <Row>
              {this.renderHeader('People', {
                disabled: districtIsSelected,
                hidden: isUrgentMessage,
              })}
              <ul>
                {recentIndividualResults.map(individual => (
                  <RecipientItem
                    key={individual[0].id}
                    recipient={individual}
                    recipientIsSelected={this.checkIfRecipientIsSelected(individual)}
                    selectionLength={selection.length}
                    districtIsSelected={districtIsSelected}
                  />
                ))}
                {/* Because v2/chats/members/potential is the slowest query, we
                  don't gate all of the other results on it */}
                {loadingPeopleResults && (
                  <li className="RecipientItem">
                    <CenteredLoadingIndicator />
                  </li>
                )}
              </ul>
            </Row>
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  };

  render() {
    const {
      selection,
      totalRecipientCount,
      loadingGroupResults,
      recipientCountsMap,
      uiEventContext,
    } = this.props;

    return (
      <React.Fragment>
        <div className="SelectFromList-main">
          <SearchBlock
            ref={ref => {
              if (ref && ref.handleClose) {
                this._searchBlock = ref;
              }
            }}
            className="SelectFromList-main-search"
            label="Search for people or classes"
            onDismiss={this.handleQueryChange}
            onQueryChange={this.handleQueryChange}
            onClick={() =>
              EventTracking.trackKey('announcements.share_to.search.click', uiEventContext)
            }
          />
          <ScrollContainer>
            {loadingGroupResults ? <CenteredLoadingIndicator /> : this.renderResultsList()}
          </ScrollContainer>
        </div>
        <div className="SelectFromList-sidebar">
          <ScrollContainer>
            <React.Fragment>
              <Row size="xsmall">
                <MediaObject figure="right" center>
                  <Heading level="4">Selected</Heading>
                  <Heading level="4">{totalRecipientCount || ''}</Heading>
                </MediaObject>
              </Row>
              {selection.length ? (
                <ul>
                  <CSSTransitionGroup
                    transitionName="SelectFromList-selectionItem-transition"
                    transitionEnterTimeout={200}
                    transitionLeaveTimeout={200}>
                    {selection.map(s => (
                      <RecipientSelectionItem
                        key={s.id}
                        selection={s}
                        count={recipientCountsMap.get(`${s.id}`)}
                      />
                    ))}
                  </CSSTransitionGroup>
                </ul>
              ) : null}
            </React.Fragment>
          </ScrollContainer>
          <div className="SelectFromList-sidebar-footer">
            <Button
              onClick={() => {
                EventTracking.trackKey('announcements.share_to.next.click', uiEventContext);
                MetricTracking.increment('composer.search.view.ctr.success', {
                  tags: {
                    is_urgent: boolTagValue(this.props.isUrgentMessage),
                    did_type_search_query: boolTagValue(this.state.totalCharacterMutations > 0),
                  },
                });
                MetricTracking.count(
                  'composer.search.view.ctr.success.character_count',
                  this.state.totalCharacterMutations,
                  {
                    tags: {
                      is_urgent: boolTagValue(this.props.isUrgentMessage),
                    },
                  }
                );

                this.props.onContinue();
              }}
              disabled={!selection.length}
              data-test-selector="split-composer-continue-button">
              Continue
            </Button>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: {
  roleBasedMessagingSearch: RBMState;
}): ReduxStateProps => {
  const { roleBasedMessagingSearch } = state;
  return {
    results: roleBasedMessagingSearch.results,
    query: roleBasedMessagingSearch.query,
    selection: roleBasedMessagingSearch.selection,
    districtIsSelected: !!roleBasedMessagingSearch.districtIsSelected,
    savedMessage: roleBasedMessagingSearch.message,
    totalRecipientCount: roleBasedMessagingSearch.totalRecipientCount,
    recipientCountsMap: roleBasedMessagingSearch.recipientCountsMap,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps: Props): ReduxDispatchProps => {
  return bindActionCreators(
    {
      changeQuery,
      loadGroupChats,
      loadPotentialChatMembers,
      searchGroupChats,
      selectRecipient,
      flush,
    },
    dispatch
  );
};

export { SelectFromList };

export default connect(mapStateToProps, mapDispatchToProps)(SelectFromList);
